// Dashboard "Recent Activity" widget — real platform-event feed.
//
// Replaces the old demo data. Fetches GET /api/portal/activity (last 15 events
// logged by the backend's logActivity producers) and prepends new ones live via
// the `activity.created` WS event. Each row is clickable → its linkPath (CRM
// record, messages, calendar, …).

import type { PageContext } from "../../shell/types";
import * as realtime from "../../shell/realtime";
import { iconMarkup, type IconName } from "@tkos/shared-ui";

interface ActivityRow {
  id: string;
  type: string;
  text: string;
  linkPath: string | null;
  entityId: string | null;
  createdAt: string;
}

const ICON: Record<string, IconName> = {
  "lead-received": "customer",
  "customer-created": "customer",
  "quote-sent": "file",
  "quote-accepted": "check",
  "payment-requested": "pound",
  "payment-received": "pound",
  "message-received": "chat",
  "message-sent": "chat",
  "stage-changed": "arrow-right",
  "delivery-booked": "truck",
  "appointment-booked": "calendar",
  "snag-logged": "alert-triangle",
  "snag-resolved": "check",
  "document-uploaded": "file",
  "portal-accessed": "customer",
};
const TONE: Record<string, string> = {
  "quote-accepted": "ok", "payment-received": "money", "payment-requested": "money",
  "snag-logged": "warn", "snag-resolved": "ok", "message-received": "info",
};

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch] as string));
}
function relTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d < 30 ? `${d}d ago` : `${Math.round(d / 30)}mo ago`;
}
function toneBg(tone: string): string {
  switch (tone) {
    case "ok": return "rgba(34,197,94,0.15)";
    case "money": return "rgba(229,130,0,0.15)";
    case "warn": return "rgba(245,158,11,0.15)";
    case "danger": return "rgba(239,68,68,0.15)";
    default: return "rgba(59,130,246,0.15)";
  }
}

/** Mount the Recent Activity widget. Returns a cleanup fn. */
export function mountActivityWidget(host: HTMLElement, ctx: PageContext, retailerId: string): () => void {
  let destroyed = false;

  const load = async (): Promise<void> => {
    let events: ActivityRow[] = [];
    try {
      const resp = await ctx.api<{ events?: ActivityRow[] }>(
        `/portal/activity?retailerId=${encodeURIComponent(retailerId)}`,
      );
      events = resp.events ?? [];
    } catch { events = []; }
    if (destroyed) return;
    if (events.length === 0) {
      host.innerHTML = `<li class="dash-empty">No recent activity.</li>`;
      return;
    }
    host.innerHTML = events.map((e) => {
      const tone = TONE[e.type] ?? "info";
      const icon = ICON[e.type] ?? "sparkle";
      const clickable = !!e.linkPath;
      return `
        <li class="dash-act${clickable ? " dash-act--link" : ""}" ${clickable ? `data-link="${esc(e.linkPath as string)}"` : ""} ${e.entityId ? `data-entity="${esc(e.entityId)}"` : ""} ${clickable ? 'style="cursor:pointer"' : ""}>
          <span class="dash-act__ico" style="background:${toneBg(tone)}" aria-hidden="true">${iconMarkup(icon, { size: "sm" })}</span>
          <div class="dash-act__body">
            <div class="dash-act__text">${esc(e.text)}</div>
            <div class="dash-act__when">${esc(relTime(e.createdAt))}</div>
          </div>
        </li>`;
    }).join("");
    host.querySelectorAll<HTMLElement>(".dash-act--link").forEach((li) => {
      li.addEventListener("click", () => {
        const link = li.dataset["link"];
        if (!link) return;
        const page = link.replace(/^\//, "").split("?")[0] ?? "";
        const entity = li.dataset["entity"];
        // CRM rows deep-link to the customer record when we have an id.
        if (page === "crm" && entity) ctx.navigate("crm", { focus: entity });
        else if (page) ctx.navigate(page);
      });
    });
  };

  const off = realtime.onEvent("activity.created", () => { if (!destroyed) void load(); });
  void load();

  return () => { destroyed = true; off(); host.innerHTML = ""; };
}
